import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/*
 * Cart drawer additions (snippets/lainly-cart-top.liquid, snippets/lainly-cart-bundle.liquid):
 * <lainly-cart-timer>: the "cart is reserved" countdown. It runs down from the theme setting's minutes,
 *   remembered for the browser tab; at zero the block goes away, and every piece added to the cart starts
 *   it again from the full time.
 * <lainly-cart-bundle>: the bundle upsell. Its slots follow the cart: 3 for a new Buy 1 Get 2 Free, or the
 *   free pieces left to complete the cart's last bundle. They are filled from collection tabs and a row of
 *   pieces ("+ Add" on each card, with its color and ring size); the moment the last slot fills, the pieces go
 *   into the cart as one bundle. Pieces that match one already in the cart (set in the theme settings) come
 *   first with a "Match It" tag; the theme's "Almost sold out" pieces carry that strip. The pieces load on
 *   demand from sections/lainly-cart-upsell.liquid.
 * <lainly-cart-six> (snippets/lainly-cart-six.liquid): with exactly 3 pieces, "Make it 6, get 4 free" at the top
 *   of the cart. "Yes, pick 3 more" opens the cart's "Pick your pieces" page (sections/lainly-cart-pick.liquid),
 *   the product page's page with a bundle of its own; the 3 pieces picked there go straight into the cart.
 * On desktop the page behind the open cart is dimmed; tapping the dimmed page closes the cart.
 */

const GROUP_SIZE = 3;
const FREE_PER_GROUP = 2;
const TIMER_KEY = 'lainly-cart-deadline';
const CLOSE_ICON =
  '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>';
const CHECK_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="m5 12 5 5L20 7" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** The small "● Almost sold out" line under a bundle card's price (styles: snippets/lainly-low-stock-styles.liquid). */
function lowLine(label) {
  const line = Object.assign(document.createElement('span'), { className: 'lainly-low-line lainly-low-line--card' });
  line.append(Object.assign(document.createElement('span'), { className: 'lainly-low-dot' }), label);
  return line;
}

/** A bundle card's name with its last word on a second line, so every card's name takes two lines. */
function twoLineTitle(title) {
  const cut = title.trim().lastIndexOf(' ');
  return cut > 0 ? `${title.slice(0, cut)}\n${title.slice(cut + 1)}` : title;
}

function readJson(root, selector, fallback) {
  try {
    return JSON.parse(root.querySelector(selector)?.textContent || '');
  } catch {
    return fallback;
  }
}

function formatNumber(value, decimals, thousands, decimal) {
  const [whole, fraction] = value.toFixed(decimals).split('.');
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
}

/** Formats cents with the shop's money format, e.g. "${{amount}}". */
function formatMoney(cents, format) {
  const value = cents / 100;
  return format
    .replace(/<[^>]*>/g, '')
    .replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return formatNumber(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return formatNumber(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatNumber(value, 0, '.', ',');
        default:
          return formatNumber(value, 2, ',', '.');
      }
    });
}

/* ---------- Reserved countdown ---------- */

const MINUTES_KEY = 'lainly-cart-minutes';
const RESTART_EVENT = 'lainly:cart-timer-restart';

function readNumber(key) {
  try {
    return Number(sessionStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function writeNumber(key, value) {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    // Storage blocked: the countdown lasts for this page only.
  }
}

/** Starts the countdown again from the full time (the theme setting's minutes, 5 by default). */
function restartCountdown() {
  const minutes = readNumber(MINUTES_KEY) || 5;
  writeNumber(TIMER_KEY, Date.now() + minutes * 60 * 1000);
  document.dispatchEvent(new CustomEvent(RESTART_EVENT));
}

// Every piece added to the cart, from anywhere on the site, starts the countdown again. This listens at the
// page level, so it also works when the cart was empty and the timer is not drawn yet.
if (!window.lainlyCartTimerReady) {
  window.lainlyCartTimerReady = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const action = /** @type {any} */ (event).action ?? /** @type {any} */ (event).detail?.action;
    if (action === 'add') restartCountdown();
  });
}

class LainlyCartTimer extends HTMLElement {
  #interval = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    writeNumber(MINUTES_KEY, Math.max(1, Number(this.dataset.minutes) || 5));
    // The first time a cart is shown in this visit, the countdown starts.
    if (!readNumber(TIMER_KEY)) restartCountdown();

    this.#abort = new AbortController();
    document.addEventListener(RESTART_EVENT, () => this.#start(), { signal: this.#abort.signal });
    this.#start();
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    this.#abort?.abort();
  }

  #start() {
    clearInterval(this.#interval);
    this.#tick();
    if (!this.hidden) this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  /** Shows the time left; at zero the block goes away until the next piece is added. */
  #tick() {
    const left = Math.max(0, Math.round((readNumber(TIMER_KEY) - Date.now()) / 1000));
    this.hidden = left === 0;
    if (left === 0) {
      clearInterval(this.#interval);
      return;
    }
    const minutes = String(Math.floor(left / 60)).padStart(2, '0');
    const seconds = String(left % 60).padStart(2, '0');
    const time = this.querySelector('[data-timer]');
    if (time) time.textContent = `${minutes}:${seconds}`;
  }
}

if (!customElements.get('lainly-cart-timer')) customElements.define('lainly-cart-timer', LainlyCartTimer);

/* ---------- Desktop: tapping the dimmed page closes the cart ---------- */

if (!window.lainlyCartBackdropReady) {
  window.lainlyCartBackdropReady = true;
  document.addEventListener('click', (event) => {
    // The dim is drawn on the page body itself, so a tap on it reaches the body.
    if (event.target !== document.body || !window.matchMedia('(min-width: 990px)').matches) return;
    const drawer = document.getElementById('cart-drawer');
    if (drawer?.hasAttribute('open') && 'close' in drawer && typeof drawer.close === 'function') drawer.close();
  });
}

/* ---------- Bundle upsell ---------- */

const UPSELL_KEY = 'lainly-cart-upsell';
const UPSELL_TTL = 30 * 60 * 1000;

/**
 * @typedef {{id: number, options: string[], price: number, compare: number, available: boolean, image: string}} Variant
 * @typedef {{id: number, title: string, url: string, image: string, options: string[], colorIndex: number, variants: Variant[]}} Product
 * @typedef {{label: string, products: Product[]}} Group
 * @typedef {{productId: number, values: string[]}} Piece
 */

/** The upsell's pieces by collection, from sections/lainly-cart-upsell.liquid; kept for the visit. */
async function loadUpsellGroups() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(UPSELL_KEY) || 'null');
    if (cached && Date.now() - cached.time < UPSELL_TTL && Array.isArray(cached.groups)) return cached.groups;
  } catch {
    // No cache: fetch below.
  }
  const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
  const response = await fetch(`${root}?section_id=lainly-cart-upsell`);
  if (!response.ok) return [];
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const groups = readJson(html, '[data-cart-upsell-groups]', []).filter((group) => group.products?.length);
  try {
    sessionStorage.setItem(UPSELL_KEY, JSON.stringify({ time: Date.now(), groups }));
  } catch {
    // Storage full or blocked: it is fetched again next time.
  }
  return groups;
}

class LainlyCartBundle extends HTMLElement {
  static observedAttributes = ['data-cart-count', 'data-ship-remaining', 'data-cart-products'];

  /** @type {Group[]} */
  #groups = [];
  /** @type {Map<number, Product>} */
  #byId = new Map();
  /** @type {Record<string, string>} */
  #swatches = {};
  /** @type {Record<string, string>} */
  #text = {};
  #tab = 0;
  /** The slots, null while empty; 3 for a new bundle, or the free pieces left to complete one. @type {Array<Piece | null>} */
  #slots = [null, null, null];
  /** Choices made on the cards, by product id. @type {Map<number, string[]>} */
  #cardValues = new Map();
  #justAdded = -1;
  /** True while the full slots are being added to the cart. */
  #busy = false;
  /** "Match It" pairs from the theme settings: a piece, the pieces in the cart it goes with, and whether it always leads. @type {Array<{id: number, with: number[], lead: boolean}>} */
  #matches = [];
  /** The matched pieces the row was last drawn with, so it is only redrawn when they change. */
  #matchedKey = '';
  /** "… is almost sold out" shown in the progress line for a moment after one of those pieces is added. */
  #lowNote = '';
  #lowNoteTimer = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ResizeObserver | null} */
  #resize = null;
  /** Photo viewer: the piece shown, its photos, the one in view, and photos already downloaded. */
  #viewer = { productId: 0, images: /** @type {string[]} */ ([]), index: 0, loaded: new Set(), touchX: /** @type {number | null} */ (null) };

  connectedCallback() {
    this.#swatches = readJson(this, '[data-cart-bundle-swatches]', {});
    this.#matches = readJson(this, '[data-cart-bundle-match]', []);
    // Translations arrive HTML-escaped ("they&#39;re"); turn them back into plain text.
    const decoder = document.createElement('textarea');
    this.#text = Object.fromEntries(
      Object.entries(readJson(this, '[data-cart-bundle-text]', {})).map(([key, value]) => {
        decoder.innerHTML = String(value);
        return [key, decoder.value];
      })
    );

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    const shelf = this.querySelector('[data-cart-bundle-shelf]');
    shelf?.addEventListener('scroll', () => this.#updateShelfControls(), { passive: true, signal });
    // The cart may still be closed when this is drawn; measure the row again whenever it gets its size.
    if (shelf) {
      this.#resize = new ResizeObserver(() => this.#updateShelfControls());
      this.#resize.observe(shelf);
    }
    this.#bindBar(signal);
    this.#bindViewer(signal);

    this.#fitSlots();
    this.#render();
    loadUpsellGroups()
      .then((groups) => {
        this.#groups = groups;
        for (const group of groups) for (const product of group.products) this.#byId.set(product.id, product);
        this.#renderTabs();
        this.#renderShelf();
        this.#render();
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
  }

  /**
   * The cart re-rendered with a new count or total: resize the slots and redraw the line. When its pieces
   * change which "Match It" pieces apply, the row is redrawn with their tags, scrolled where it was.
   */
  attributeChangedCallback(name) {
    if (!this.#abort) return;
    if (name === 'data-cart-products') {
      if (this.#groups.length && this.#matchedIds().join() !== this.#matchedKey) this.#renderShelf({ keepScroll: true });
      return;
    }
    this.#fitSlots();
    this.#render();
  }

  /** "Match It" pieces whose partner is in the cart and that aren't in it yet, in the settings' order. */
  #matchedIds() {
    const inCart = new Set(String(this.dataset.cartProducts || '').split(',').map(Number));
    return this.#matches
      .filter((match) => !inCart.has(match.id) && match.with.some((id) => inCart.has(id)))
      .map((match) => match.id);
  }

  get #format() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  #t(key, values = {}) {
    return Object.entries(values).reduce(
      (text, [name, value]) => text.replaceAll(`[${name}]`, String(value)),
      this.#text[key] || ''
    );
  }

  /** Pieces needed: 3 for a new Buy 1 Get 2 Free, or the free pieces left to complete the cart's last bundle. */
  get #needed() {
    const inCart = Number(this.dataset.cartCount) || 0;
    const rest = inCart % GROUP_SIZE;
    return rest === 0 ? GROUP_SIZE : GROUP_SIZE - rest;
  }

  /** Matches the slots to what is needed, keeping pieces already picked. */
  #fitSlots() {
    const needed = this.#needed;
    const picked = this.#slots.filter(Boolean).slice(0, needed);
    this.#slots = [...picked, ...Array(needed - picked.length).fill(null)];
  }

  #optionValues(product, index) {
    return [...new Set(product.variants.map((variant) => variant.options[index]))];
  }

  /** A card's choices: what the shopper picked on it, or its first available color and nothing else yet. */
  #valuesFor(product) {
    const saved = this.#cardValues.get(product.id);
    if (saved) return [...saved];
    const first = product.variants.find((variant) => variant.available) || product.variants[0];
    return product.options.map((_, index) => {
      const values = this.#optionValues(product, index);
      if (values.length === 1) return values[0];
      if (index === product.colorIndex) return first?.options[index] ?? values[0];
      return '';
    });
  }

  /** @returns {Variant | null} */
  #variantFor(piece) {
    const product = this.#byId.get(piece.productId);
    return (
      product?.variants.find(
        (variant) => variant.available && variant.options.every((value, index) => value === piece.values[index])
      ) || null
    );
  }

  /** Price and photo for a piece even before every choice is made. */
  #priceFor(piece) {
    const product = this.#byId.get(piece.productId);
    if (!product) return { price: 0, image: '' };
    const near =
      this.#variantFor(piece) ||
      product.variants.find((variant) =>
        variant.options.every((value, index) => !piece.values[index] || value === piece.values[index])
      ) ||
      product.variants[0];
    return { price: near?.price ?? 0, image: near?.image || product.image };
  }

  #swatchFor(color) {
    const key = String(color || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return this.#swatches[key] || '';
  }

  get #filled() {
    return this.#slots.filter(Boolean).length;
  }

  /** Slot prices: picked pieces at their price, empty slots at the cheapest picked piece's (null if none). */
  #slotPrices() {
    const picked = /** @type {Piece[]} */ (this.#slots.filter(Boolean)).map((piece) => this.#priceFor(piece).price);
    const cheapest = picked.length ? Math.min(...picked) : null;
    return this.#slots.map((piece) => (piece ? this.#priceFor(piece).price : cheapest));
  }

  /** Free slots: all of them when completing a bundle, otherwise the cheapest 2 of the 3. */
  #freeIndexes() {
    if (this.#slots.length < GROUP_SIZE) return new Set(this.#slots.map((_, index) => index));
    return new Set(
      this.#slotPrices()
        .map((price, index) => ({ price: price ?? 0, index }))
        .sort((a, b) => a.price - b.price || b.index - a.index)
        .slice(0, FREE_PER_GROUP)
        .map(({ index }) => index)
    );
  }

  /** "Pick 3 pieces", "Pick 2 free pieces", "Pick 1 free piece". */
  get #pickText() {
    const missing = this.#slots.length - this.#filled;
    if (missing === GROUP_SIZE) return this.#t('pick_all', { count: missing });
    return missing === 1 ? this.#t('free_one') : this.#t('free_other', { count: missing });
  }

  /**
   * The line under the heading, from the cart: "Add 3 more pieces, pay for just 1." (and "get Free Shipping"
   * when one more paid piece reaches it), or the free pieces left to complete a bundle.
   */
  get #lineText() {
    const needed = this.#needed;
    if (needed === 1) return this.#t('line_one');
    if (needed === 2) return this.#t('line_two');
    const remaining = Number(this.dataset.shipRemaining);
    const prices = this.#groups.flatMap((group) => group.products.map((product) => product.variants[0]?.price ?? 0));
    const cheapest = prices.length ? Math.min(...prices) : Infinity;
    return remaining > 0 && cheapest >= remaining ? this.#t('line_three_ship') : this.#t('line_three');
  }

  /* ----- Events ----- */

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const viewer = this.querySelector('[data-viewer]');
    if (viewer instanceof HTMLDialogElement && viewer.contains(target)) {
      // The cart closes on clicks outside its panel; a click in (or around) the viewer must not reach it.
      event.stopPropagation();
      const product = this.#byId.get(this.#viewer.productId);
      const color = target.closest('[data-viewer-color]');
      const photoStep = target.closest('[data-viewer-step]');
      const thumb = target.closest('[data-viewer-thumb]');
      if (color instanceof HTMLElement && product) this.#chooseInViewer(product.colorIndex, color.dataset.viewerColor || '');
      else if (photoStep instanceof HTMLElement) this.#showPhoto(this.#viewer.index + Number(photoStep.dataset.viewerStep), Number(photoStep.dataset.viewerStep));
      else if (thumb instanceof HTMLElement) this.#showPhoto(Number(thumb.dataset.viewerThumb), Math.sign(Number(thumb.dataset.viewerThumb) - this.#viewer.index));
      else if (target.closest('[data-viewer-add]')) this.#addFromViewer();
      // The close button, or a tap on the dark backdrop around the panel.
      else if (target.closest('[data-viewer-close]') || target === viewer) this.#closeViewer();
      return;
    }

    const zoom = target.closest('[data-cart-bundle-zoom]');
    if (zoom instanceof HTMLElement) {
      const product = this.#productOf(zoom);
      if (product) this.#openViewer(product.id);
      return;
    }

    const tab = target.closest('[data-cart-bundle-tab]');
    if (tab instanceof HTMLElement) {
      this.#tab = Number(tab.dataset.cartBundleTab);
      for (const other of this.querySelectorAll('[data-cart-bundle-tab]')) other.setAttribute('aria-pressed', String(other === tab));
      this.#renderShelf();
      return;
    }

    const color = target.closest('[data-cart-bundle-color]');
    if (color instanceof HTMLButtonElement) {
      const product = this.#productOf(color);
      if (!product) return;
      const values = this.#valuesFor(product);
      values[product.colorIndex] = color.dataset.cartBundleColor || '';
      this.#cardValues.set(product.id, values);
      this.#refreshCard(product.id);
      return;
    }

    const add = target.closest('[data-cart-bundle-add]');
    if (add instanceof HTMLButtonElement) {
      const product = this.#productOf(add);
      if (product) this.#addPiece(product);
      return;
    }

    const remove = target.closest('[data-cart-bundle-remove]');
    if (remove instanceof HTMLElement) {
      if (this.#busy) return;
      const index = Number(remove.dataset.cartBundleRemove);
      this.#slots.splice(index, 1);
      this.#slots.push(null);
      this.#showError('');
      this.#render();
      return;
    }

    const step = target.closest('[data-cart-bundle-step]');
    if (step instanceof HTMLElement) {
      const shelf = this.querySelector('[data-cart-bundle-shelf]');
      const card = shelf?.querySelector('[data-cart-bundle-card]');
      if (shelf instanceof HTMLElement) {
        const gap = parseFloat(getComputedStyle(shelf).columnGap) || 0;
        const distance = card instanceof HTMLElement ? card.offsetWidth + gap : shelf.clientWidth * 0.8;
        const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
        shelf.scrollBy({ left: Number(step.dataset.cartBundleStep) * distance, behavior: smooth ? 'smooth' : 'instant' });
      }
    }
  };

  #onChange = (event) => {
    const select = event.target;
    if (select instanceof HTMLSelectElement && select.matches('[data-viewer-option]')) {
      select.classList.remove('is-invalid');
      this.#chooseInViewer(Number(select.dataset.viewerOption), select.value);
      return;
    }
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-cart-bundle-option]')) return;
    const product = this.#productOf(select);
    if (!product) return;
    const values = this.#valuesFor(product);
    values[Number(select.dataset.cartBundleOption)] = select.value;
    this.#cardValues.set(product.id, values);
    this.#refreshCard(product.id);
  };

  #productOf(element) {
    const card = element.closest('[data-cart-bundle-card]');
    return card instanceof HTMLElement ? this.#byId.get(Number(card.dataset.cartBundleCard)) : undefined;
  }

  /**
   * Puts a piece in the first empty slot once its choices are made; the last one sends the bundle to the
   * cart right after its slot fills. @returns {boolean} added
   */
  #addPiece(product) {
    if (this.#busy) return false;
    const values = this.#valuesFor(product);
    const card = this.querySelector(`[data-cart-bundle-card="${product.id}"]`);
    const error = card?.querySelector('[data-cart-bundle-card-error]');
    const showCardError = (message) => {
      if (!(error instanceof HTMLElement)) return;
      error.textContent = message;
      error.hidden = !message;
    };

    const missing = product.options.findIndex((_, i) => this.#optionValues(product, i).length > 1 && !values[i]);
    if (missing >= 0) {
      const select = card?.querySelector(`[data-cart-bundle-option="${missing}"]`);
      if (select instanceof HTMLSelectElement) {
        select.classList.add('is-invalid');
        select.focus();
      }
      showCardError(this.#t('choose', { name: product.options[missing].toLowerCase() }));
      return false;
    }
    if (!this.#variantFor({ productId: product.id, values })) {
      showCardError(this.#t('sold_out'));
      return false;
    }

    const slot = this.#slots.indexOf(null);
    if (slot < 0) return false;
    this.#slots[slot] = { productId: product.id, values: [...values] };
    this.#justAdded = slot;
    this.#showError('');
    this.#noteLowStock(product);
    this.#render();

    const button = this.querySelector(`[data-cart-bundle-card="${product.id}"] [data-cart-bundle-add]`);
    if (button instanceof HTMLButtonElement && !button.disabled) {
      button.innerHTML = `${CHECK_ICON}<span>${this.#t('added')}</span>`;
      button.classList.add('is-added');
      setTimeout(() => this.#refreshCard(product.id), 900);
    }
    // A short pause so the shopper sees the last slot fill before the pieces move into the cart.
    if (this.#filled === this.#slots.length) setTimeout(() => this.#submit(), 450);
    return true;
  }

  /**
   * After an "Almost sold out" piece goes in, and while slots are still empty, the progress line says so
   * for a few seconds before going back to what is left to pick.
   */
  #noteLowStock(product) {
    clearTimeout(this.#lowNoteTimer);
    this.#lowNote = product.low && this.#filled < this.#slots.length ? this.#t('low_note', { name: product.title }) : '';
    if (!this.#lowNote) return;
    this.#lowNoteTimer = window.setTimeout(() => {
      this.#lowNote = '';
      this.#render();
    }, 3500);
  }

  /* ----- Photo viewer (the same one as the product page) ----- */

  #bindViewer(signal) {
    const viewer = this.querySelector('[data-viewer]');
    if (!(viewer instanceof HTMLDialogElement)) return;
    // Escape closes only the viewer, not the cart behind it.
    viewer.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.#closeViewer();
        }
        if (event.key === 'ArrowRight') this.#showPhoto(this.#viewer.index + 1, 1);
        if (event.key === 'ArrowLeft') this.#showPhoto(this.#viewer.index - 1, -1);
      },
      { signal }
    );
    viewer.addEventListener('cancel', (event) => event.preventDefault(), { signal });
    const stage = viewer.querySelector('[data-viewer-stage]');
    stage?.addEventListener('touchstart', (event) => (this.#viewer.touchX = event.touches[0].clientX), { passive: true, signal });
    stage?.addEventListener(
      'touchend',
      (event) => {
        if (this.#viewer.touchX == null) return;
        const distance = event.changedTouches[0].clientX - this.#viewer.touchX;
        this.#viewer.touchX = null;
        if (Math.abs(distance) > 40) this.#showPhoto(this.#viewer.index + (distance < 0 ? 1 : -1), distance < 0 ? 1 : -1);
      },
      { passive: true, signal }
    );
  }

  /** Opens a piece's photos large, starting on the photo of its chosen color. */
  #openViewer(productId) {
    const viewer = this.querySelector('[data-viewer]');
    const product = this.#byId.get(productId);
    if (!(viewer instanceof HTMLDialogElement) || !product) return;

    this.#viewer.productId = productId;
    const title = viewer.querySelector('[data-viewer-title]');
    if (title) title.textContent = product.title;
    if (title instanceof HTMLAnchorElement && product.url) title.href = product.url;
    this.#showViewerError('');
    this.#renderViewerInfo();
    this.#showPhoto(this.#setViewerImages(product));
    if (!viewer.open) viewer.showModal();
    // Load the other photos now, so moving through them is instant.
    for (const url of this.#viewer.images) this.#preload(url);
  }

  /** The viewer's photos and thumbnails, making sure the chosen color's photo is among them. @returns {number} */
  #setViewerImages(product) {
    const viewer = this.querySelector('[data-viewer]');
    const path = (url) => String(url).split('?')[0];
    const { image } = this.#priceFor({ productId: product.id, values: this.#valuesFor(product) });
    const images = /** @type {any} */ (product).images?.length ? [.../** @type {any} */ (product).images] : [];
    let start = images.findIndex((url) => path(url) === path(image));
    if (start < 0 && image) {
      images.unshift(String(image).replace(/([?&])width=\d+/, '$1width=900'));
      start = 0;
    }
    const changed = images.join() !== this.#viewer.images.join();
    this.#viewer.images = images;
    if (!viewer || !changed) return Math.max(0, start);

    viewer.querySelector('[data-viewer-thumbs]')?.replaceChildren(
      ...(images.length > 1 ? images : []).map((url, index) => {
        const item = document.createElement('li');
        const button = Object.assign(document.createElement('button'), { type: 'button', className: 'lainly-viewer__thumb' });
        button.dataset.viewerThumb = String(index);
        button.setAttribute('aria-label', this.#t('photo', { number: index + 1 }));
        const img = Object.assign(document.createElement('img'), { src: this.#thumbUrl(url), alt: '' });
        img.width = 160;
        img.height = 160;
        button.append(img);
        item.append(button);
        return item;
      })
    );
    for (const stepButton of viewer.querySelectorAll('[data-viewer-step]')) {
      if (stepButton instanceof HTMLElement) stepButton.hidden = images.length < 2;
    }
    return Math.max(0, start);
  }

  /** The viewer's price, choices (color dots, ring size) and Add button, from the piece's card. */
  #renderViewerInfo() {
    const viewer = this.querySelector('[data-viewer]');
    const product = this.#byId.get(this.#viewer.productId);
    if (!viewer || !product) return;
    const values = this.#valuesFor(product);
    const { price } = this.#priceFor({ productId: product.id, values });
    const priceEl = viewer.querySelector('[data-viewer-price]');
    if (priceEl) priceEl.textContent = formatMoney(price, this.#format);
    viewer.querySelector('[data-viewer-low]')?.toggleAttribute('hidden', !product.low);

    viewer.querySelector('[data-viewer-options]')?.replaceChildren(
      ...product.options.flatMap((name, index) => {
        const optionValues = this.#optionValues(product, index);
        if (optionValues.length <= 1) return [];
        if (index !== product.colorIndex) {
          const select = Object.assign(document.createElement('select'), { className: 'lainly-viewer__select' });
          select.dataset.viewerOption = String(index);
          select.setAttribute('aria-label', name);
          select.append(
            Object.assign(document.createElement('option'), {
              value: '',
              disabled: true,
              textContent: this.#t('choose', { name: name.toLowerCase() }),
            }),
            ...optionValues.map((value) => Object.assign(document.createElement('option'), { value, textContent: value }))
          );
          select.value = values[index] || '';
          return [select];
        }
        // "Color: Gold", then a dot per color.
        const label = Object.assign(document.createElement('span'), { className: 'lainly-viewer__label', textContent: `${name}: ` });
        label.append(Object.assign(document.createElement('strong'), { textContent: values[index] || '' }));
        const dots = Object.assign(document.createElement('span'), { className: 'lainly-viewer__colors' });
        dots.setAttribute('role', 'group');
        dots.setAttribute('aria-label', name);
        for (const value of optionValues) {
          const available = product.variants.some((variant) => variant.available && variant.options[index] === value);
          const button = Object.assign(document.createElement('button'), { type: 'button', className: 'lainly-viewer__color', disabled: !available });
          button.dataset.viewerColor = value;
          button.setAttribute('aria-label', value);
          button.setAttribute('aria-pressed', String(values[index] === value));
          const swatch = this.#swatchFor(value) || product.variants.find((variant) => variant.options[index] === value)?.image || '';
          if (swatch) {
            const dot = Object.assign(document.createElement('img'), { src: swatch, alt: '' });
            dot.width = 30;
            dot.height = 30;
            button.append(dot);
          }
          dots.append(button);
        }
        return [label, dots];
      })
    );

    const add = viewer.querySelector('[data-viewer-add]');
    if (add instanceof HTMLButtonElement) {
      const full = this.#filled === this.#slots.length;
      add.disabled = full;
      add.classList.remove('is-added');
      // Only the label changes; the guarantee line under it stays.
      const label = add.querySelector('[data-viewer-add-label]') || add;
      label.textContent = full ? this.#t('full') : `+ ${this.#t('add_to_bundle')}`;
    }
  }

  /** A choice made in the viewer: saved on the piece's card too; a new color jumps to its photo. */
  #chooseInViewer(optionIndex, value) {
    const product = this.#byId.get(this.#viewer.productId);
    if (!product) return;
    const values = this.#valuesFor(product);
    values[optionIndex] = value;
    this.#cardValues.set(product.id, values);
    this.#refreshCard(product.id);
    this.#showViewerError('');
    this.#renderViewerInfo();
    if (optionIndex === product.colorIndex) this.#showPhoto(this.#setViewerImages(product));
  }

  #showViewerError(message) {
    const error = this.querySelector('[data-viewer-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /**
   * Shows photo `index` (wrapping around): the small copy at once, then the sharp one when it is ready.
   * @param {number} index
   * @param {number} [slide] - 1 or -1 slides the new photo in from that side, like the product gallery; 0 shows it
   *   at once (opening the viewer, picking a color).
   */
  #showPhoto(index, slide = 0) {
    const viewer = this.querySelector('[data-viewer]');
    const { images } = this.#viewer;
    if (!viewer || !images.length) return;
    const shown = (index + images.length) % images.length;
    this.#viewer.index = shown;

    const image = viewer.querySelector('[data-viewer-image]');
    if (image instanceof HTMLImageElement) {
      const url = images[shown];
      const product = this.#byId.get(this.#viewer.productId);
      image.alt = product ? `${product.title}, ${this.#t('photo', { number: shown + 1 })}` : '';
      if (this.#viewer.loaded.has(url)) {
        image.src = url;
      } else {
        image.src = this.#thumbUrl(url);
        this.#preload(url).then(() => {
          if (this.#viewer.index === shown && this.#viewer.images[shown] === url) image.src = url;
        });
      }
    }
    if (slide && image instanceof HTMLImageElement && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      image.animate([{ transform: `translateX(${slide > 0 ? 100 : -100}%)` }, { transform: 'none' }], {
        duration: 280,
        easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
      });
    }
    viewer.querySelectorAll('[data-viewer-thumb]').forEach((thumb, i) => {
      if (i === shown) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  /** The viewer's Add: asks for any missing choice (like ring size) right there, then adds and closes. */
  #addFromViewer() {
    const viewer = this.querySelector('[data-viewer]');
    const product = this.#byId.get(this.#viewer.productId);
    if (!(viewer instanceof HTMLDialogElement) || !product) return;

    const values = this.#valuesFor(product);
    const missing = product.options.findIndex((_, i) => this.#optionValues(product, i).length > 1 && !values[i]);
    if (missing >= 0) {
      const select = viewer.querySelector(`[data-viewer-option="${missing}"]`);
      if (select instanceof HTMLSelectElement) {
        select.classList.add('is-invalid');
        select.focus();
      }
      this.#showViewerError(this.#t('choose', { name: product.options[missing].toLowerCase() }));
      return;
    }
    if (!this.#addPiece(product)) return;
    const add = viewer.querySelector('[data-viewer-add]');
    if (add instanceof HTMLButtonElement) {
      (add.querySelector('[data-viewer-add-label]') || add).innerHTML = `${CHECK_ICON}<span>${this.#t('added')}</span>`;
      add.classList.add('is-added');
      add.disabled = true;
    }
    setTimeout(() => this.#closeViewer(), 700);
  }

  #closeViewer() {
    const viewer = this.querySelector('[data-viewer]');
    if (viewer instanceof HTMLDialogElement && viewer.open) viewer.close();
  }

  /** The same photo at thumbnail size. */
  #thumbUrl(url) {
    return String(url).replace(/([?&])width=\d+/, '$1width=400');
  }

  /** Downloads and decodes a photo once. */
  #preload(url) {
    if (this.#viewer.loaded.has(url)) return Promise.resolve();
    const image = new Image();
    image.src = url;
    return image
      .decode()
      .catch(() => {})
      .then(() => {
        this.#viewer.loaded.add(url);
      });
  }

  /* ----- Rendering ----- */

  #render() {
    const line = this.querySelector('[data-cart-bundle-line]');
    if (line) line.textContent = this.#lineText;

    this.#renderSlots();
    const full = this.#filled === this.#slots.length;

    const progress = this.querySelector('[data-cart-bundle-progress]');
    if (progress instanceof HTMLElement) {
      progress.classList.toggle('is-complete', full);
      if (full) {
        progress.innerHTML = CHECK_ICON;
        progress.append(Object.assign(document.createElement('span'), { textContent: this.#t('adding') }));
      } else if (this.#lowNote) {
        const note = Object.assign(document.createElement('span'), { className: 'lainly-low-note' });
        note.append(Object.assign(document.createElement('span'), { className: 'lainly-low-dot' }), this.#lowNote);
        progress.replaceChildren(note);
      } else {
        progress.textContent = this.#pickText;
      }
    }

    for (const button of this.querySelectorAll('[data-cart-bundle-add]')) {
      if (!(button instanceof HTMLButtonElement) || button.classList.contains('is-added')) continue;
      button.disabled = full;
      button.textContent = full ? this.#t('full') : `+ ${this.#t('add')}`;
    }
  }

  #renderSlots() {
    const list = this.querySelector('[data-cart-bundle-slots]');
    if (!(list instanceof HTMLElement)) return;
    const free = this.#freeIndexes();
    const prices = this.#slotPrices();
    list.style.setProperty('--lainly-slot-count', String(this.#slots.length));
    list.replaceChildren(
      ...this.#slots.map((piece, index) => {
        const slot = Object.assign(document.createElement('li'), { className: 'lainly-cart-bundle__slot' });
        let box;
        if (!piece) {
          slot.classList.add('is-empty');
          box = Object.assign(document.createElement('span'), { className: 'lainly-cart-bundle__slot-box', textContent: '+' });
          box.setAttribute('role', 'img');
          box.setAttribute('aria-label', this.#t('empty_slot', { number: index + 1 }));
        } else {
          const product = this.#byId.get(piece.productId);
          box = Object.assign(document.createElement('button'), { type: 'button', className: 'lainly-cart-bundle__slot-box' });
          box.dataset.cartBundleRemove = String(index);
          box.setAttribute('aria-label', this.#t('remove', { name: product?.title || '' }));
          const { image } = this.#priceFor(piece);
          if (image) {
            const img = Object.assign(document.createElement('img'), { src: image, alt: '' });
            img.width = 300;
            img.height = 300;
            box.append(img);
          }
          box.insertAdjacentHTML('beforeend', `<span class="lainly-cart-bundle__slot-x">${CLOSE_ICON}</span>`);
          if (index === this.#justAdded) slot.classList.add('is-new');
        }
        const tag = Object.assign(document.createElement('span'), { className: 'lainly-cart-bundle__slot-tag' });
        if (free.has(index)) {
          tag.classList.add('is-free');
          tag.textContent = this.#t('free');
        } else if (prices[index] != null) {
          tag.textContent = formatMoney(/** @type {number} */ (prices[index]), this.#format);
        }
        slot.append(box, tag);
        return slot;
      })
    );
    this.#justAdded = -1;
  }

  /** The collection tabs above the row. */
  #renderTabs() {
    const tabs = this.querySelector('[data-cart-bundle-tabs]');
    if (!(tabs instanceof HTMLElement)) return;
    tabs.setAttribute('aria-label', this.#t('tabs'));
    tabs.replaceChildren(
      ...(this.#groups.length > 1 ? this.#groups : []).map((group, index) => {
        const button = Object.assign(document.createElement('button'), {
          type: 'button',
          className: 'lainly-cart-bundle__tab',
          textContent: group.label,
        });
        button.dataset.cartBundleTab = String(index);
        button.setAttribute('aria-pressed', String(index === this.#tab));
        return button;
      })
    );
  }

  /**
   * The tab's pieces: the "Match It" pieces set to always show first, then the tab's own order. A piece matching
   * one in the cart shows its tag where it is, so nothing jumps when the cart changes.
   * @param {{keepScroll?: boolean}} [options] - Stay scrolled where the row was (only the tags changed).
   */
  #renderShelf({ keepScroll = false } = {}) {
    const shelf = this.querySelector('[data-cart-bundle-shelf]');
    if (!(shelf instanceof HTMLElement)) return;
    const scrolled = keepScroll ? shelf.scrollLeft : 0;
    const matched = this.#matchedIds();
    this.#matchedKey = matched.join();
    const lead = this.#matches.filter((match) => match.lead).map((match) => match.id);
    const rank = (product) => (lead.includes(product.id) ? lead.indexOf(product.id) : lead.length);
    const products = [...(this.#groups[this.#tab]?.products || [])].sort((a, b) => rank(a) - rank(b));
    shelf.replaceChildren(...products.map((product) => this.#card(product, matched.includes(product.id))));
    shelf.scrollLeft = scrolled;
    this.#updateShelfControls();
  }

  #refreshCard(productId) {
    const product = this.#byId.get(productId);
    const card = this.querySelector(`[data-cart-bundle-card="${productId}"]`);
    if (product && card) card.replaceWith(this.#card(product, this.#matchedIds().includes(productId)));
  }

  /** A card: photo (tagged "Match It" when it goes with a piece in the cart), name, price, color dots, ring size (or other choice), and "+ Add". */
  #card(product, matched = false) {
    const values = this.#valuesFor(product);
    const { price, image } = this.#priceFor({ productId: product.id, values });
    const card = Object.assign(document.createElement('li'), { className: 'lainly-cart-bundle__card' });
    card.dataset.cartBundleCard = String(product.id);

    // The photo and the name open the photo viewer.
    const media = Object.assign(document.createElement('button'), { type: 'button', className: 'lainly-cart-bundle__card-media' });
    media.dataset.cartBundleZoom = '';
    media.setAttribute('aria-label', this.#t('view_photos', { name: product.title }));
    if (image) {
      const img = Object.assign(document.createElement('img'), { src: image, alt: '', loading: 'lazy' });
      img.width = 300;
      img.height = 300;
      media.append(img);
    }
    if (matched && this.#t('match')) {
      media.append(Object.assign(document.createElement('span'), { className: 'lainly-cart-bundle__match', textContent: this.#t('match') }));
    }
    card.append(
      media,
      Object.assign(document.createElement('button'), {
        type: 'button',
        className: 'lainly-cart-bundle__card-title',
        textContent: twoLineTitle(product.title),
      }),
      Object.assign(document.createElement('span'), { className: 'lainly-cart-bundle__card-price', textContent: formatMoney(price, this.#format) })
    );
    const title = card.querySelector('.lainly-cart-bundle__card-title');
    if (title instanceof HTMLElement) title.dataset.cartBundleZoom = '';

    // The color dots, size menu and "pick a color" message share one row, so every card keeps the same rows.
    const options = Object.assign(document.createElement('span'), { className: 'lainly-cart-bundle__card-options' });
    product.options.forEach((name, index) => {
      const optionValues = this.#optionValues(product, index);
      if (optionValues.length <= 1) return;
      if (index === product.colorIndex) {
        const colors = Object.assign(document.createElement('span'), { className: 'lainly-cart-bundle__colors' });
        colors.setAttribute('role', 'group');
        colors.setAttribute('aria-label', `${name}, ${product.title}`);
        for (const value of optionValues) {
          const available = product.variants.some((variant) => variant.available && variant.options[index] === value);
          const button = Object.assign(document.createElement('button'), {
            type: 'button',
            className: 'lainly-cart-bundle__color',
            disabled: !available,
          });
          button.dataset.cartBundleColor = value;
          button.setAttribute('aria-label', value);
          button.setAttribute('aria-pressed', String(values[index] === value));
          const swatch = this.#swatchFor(value) || product.variants.find((variant) => variant.options[index] === value)?.image || '';
          if (swatch) {
            const dot = Object.assign(document.createElement('img'), { src: swatch, alt: '', loading: 'lazy' });
            dot.width = 22;
            dot.height = 22;
            button.append(dot);
          }
          colors.append(button);
        }
        options.append(colors);
      } else {
        const select = Object.assign(document.createElement('select'), { className: 'lainly-cart-bundle__select' });
        select.dataset.cartBundleOption = String(index);
        select.setAttribute('aria-label', `${name}, ${product.title}`);
        select.append(
          Object.assign(document.createElement('option'), {
            value: '',
            disabled: true,
            textContent: this.#t('choose', { name: name.toLowerCase() }),
          }),
          ...optionValues.map((value) => Object.assign(document.createElement('option'), { value, textContent: value }))
        );
        select.value = values[index] || '';
        options.append(select);
      }
    });

    const error = Object.assign(document.createElement('span'), { className: 'lainly-cart-bundle__card-error', hidden: true });
    error.dataset.cartBundleCardError = '';
    error.setAttribute('role', 'alert');

    const full = this.#filled === this.#slots.length;
    const add = Object.assign(document.createElement('button'), {
      type: 'button',
      className: 'lainly-cart-bundle__card-add',
      disabled: full,
      textContent: full ? this.#t('full') : `+ ${this.#t('add')}`,
    });
    add.dataset.cartBundleAdd = '';
    options.append(error);
    card.append(options, add);
    // "Almost sold out" sits under the Add button.
    if (product.low) card.append(lowLine(this.#t('low_label')));
    return card;
  }

  /** Arrows off at either end; the slider bar shows how much of the row is in view and where. */
  #updateShelfControls() {
    const shelf = this.querySelector('[data-cart-bundle-shelf]');
    if (!(shelf instanceof HTMLElement)) return;
    const max = shelf.scrollWidth - shelf.clientWidth;
    for (const arrow of this.querySelectorAll('[data-cart-bundle-step]')) {
      if (!(arrow instanceof HTMLButtonElement)) continue;
      arrow.disabled = Number(arrow.dataset.cartBundleStep) < 0 ? shelf.scrollLeft <= 1 : shelf.scrollLeft >= max - 1;
    }
    const bar = this.querySelector('[data-cart-bundle-bar]');
    const thumb = this.querySelector('[data-cart-bundle-thumb]');
    if (!(bar instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;
    bar.hidden = max <= 1;
    thumb.style.inlineSize = `${(shelf.clientWidth / Math.max(shelf.scrollWidth, 1)) * 100}%`;
    thumb.style.transform = `translateX(${(shelf.scrollLeft / Math.max(shelf.clientWidth, 1)) * 100}%)`;
  }

  /** Dragging or tapping the slider bar moves the row to that spot. */
  #bindBar(signal) {
    const bar = this.querySelector('[data-cart-bundle-bar]');
    const shelf = this.querySelector('[data-cart-bundle-shelf]');
    if (!(bar instanceof HTMLElement) || !(shelf instanceof HTMLElement)) return;
    const moveTo = (clientX) => {
      const rect = bar.getBoundingClientRect();
      const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      shelf.scrollLeft = fraction * shelf.scrollWidth - shelf.clientWidth / 2;
    };
    const stop = (event) => {
      if (!bar.hasPointerCapture(event.pointerId)) return;
      bar.releasePointerCapture(event.pointerId);
      shelf.style.removeProperty('scroll-snap-type');
    };
    bar.addEventListener(
      'pointerdown',
      (event) => {
        bar.setPointerCapture(event.pointerId);
        shelf.style.scrollSnapType = 'none';
        moveTo(event.clientX);
      },
      { signal }
    );
    bar.addEventListener('pointermove', (event) => bar.hasPointerCapture(event.pointerId) && moveTo(event.clientX), {
      signal,
    });
    bar.addEventListener('pointerup', stop, { signal });
    bar.addEventListener('pointercancel', stop, { signal });
  }

  #showError(message) {
    const error = this.querySelector('[data-cart-bundle-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /**
   * Adds the picked pieces to the cart as one bundle (called as soon as the slots are full); the cart
   * re-renders with them while the shopper's place in it stays put.
   */
  async #submit() {
    if (this.#busy || this.#filled < this.#slots.length) return;
    this.#busy = true;
    this.#render();
    const holdPlace = this.#holdPlace();

    const count = this.#slots.length;
    const quantities = new Map();
    for (const piece of /** @type {Piece[]} */ (this.#slots)) {
      const variant = this.#variantFor(piece);
      if (variant) quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
    }
    const tag = this.dataset.bundleName;
    const lines = [...quantities].map(([id, quantity]) => ({ id, quantity, ...(tag ? { properties: { _bundle: tag } } : {}) }));
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
      }).then((res) => res.json());
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );

      if (response.status) {
        const message = response.description || response.message || 'Something went wrong. Please try again.';
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'lainly-cart-bundle', itemCount: count },
        });
        this.#showError(message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, source: 'lainly-cart-bundle', itemCount: count, sections: response.sections },
      });
      this.#slots = this.#slots.map(() => null);
      holdPlace();
    } catch (error) {
      deferred.reject(error);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  /**
   * Keeps this upsell where the shopper sees it while the cart re-renders above it (new items make the list
   * longer). Returns a function to call once the add is done; it follows the re-render for a moment.
   */
  #holdPlace() {
    const scroller = this.closest('.cart-drawer__content');
    if (!(scroller instanceof HTMLElement)) return () => {};
    // Measured from the top of the scrolling area, which itself moves when the timer above it comes back.
    const offset = () => this.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    const before = offset();
    return () => {
      const until = performance.now() + 1500;
      const follow = () => {
        if (!this.isConnected) return;
        const shift = offset() - before;
        if (Math.abs(shift) > 1) scroller.scrollTop += shift;
        if (performance.now() < until) requestAnimationFrame(follow);
      };
      requestAnimationFrame(follow);
    };
  }
}

if (!customElements.get('lainly-cart-bundle')) customElements.define('lainly-cart-bundle', LainlyCartBundle);

/* ---------- "Make it 6": the cart's "Pick your pieces" page ---------- */

/** @type {Promise<Element | null> | null} */
let cartPick = null;

/**
 * The cart's "Pick your pieces" page, fetched once and kept at the end of the page. Its bundle keeps its picks
 * to itself (data-storage="memory"). Scripts in fetched markup don't run, so they are loaded here.
 * @param {string} pickScript - assets/lainly-pick.js
 */
function loadCartPick(pickScript) {
  cartPick ||= (async () => {
    const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?section_id=lainly-cart-pick`);
    if (!response.ok) throw new Error(`Pick page: ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const section = html.body.firstElementChild;
    if (!section?.querySelector('lainly-pick')) throw new Error('Pick page: empty');
    section.querySelector('lainly-bundle')?.setAttribute('data-storage', 'memory');

    const modules = pickScript ? [new URL(pickScript, location.href).href] : [];
    const inline = new Set();
    for (const script of section.querySelectorAll('script')) {
      const src = script.getAttribute('src');
      if (src) modules.push(new URL(src, location.href).href);
      else if (script.type !== 'application/json') inline.add(script.textContent || '');
      if (src || script.type !== 'application/json') script.remove();
    }
    document.body.append(document.adoptNode(section));
    for (const code of inline) document.body.append(Object.assign(document.createElement('script'), { textContent: code }));
    await Promise.all(modules.map((src) => import(src)));
    await customElements.whenDefined('lainly-pick');
    return document.querySelector('lainly-pick[data-bundle-scope]');
  })().catch((error) => {
    cartPick = null;
    throw error;
  });
  return cartPick;
}

class LainlyCartSix extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
    // Get the page ready while the shopper reads the card, so "Yes" opens it at once.
    const whenIdle = window.requestIdleCallback || ((callback) => setTimeout(callback, 800));
    whenIdle(() => this.isConnected && loadCartPick(this.dataset.pickScript || '').catch(() => {}));
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-six-yes]') : null;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      const pick = await loadCartPick(this.dataset.pickScript || '');
      if (pick && typeof pick.open === 'function') pick.open();
    } catch {
      // The page could not load: the first collection is the next best place to pick.
      if (this.dataset.fallbackUrl) window.location.href = this.dataset.fallbackUrl;
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('lainly-cart-six')) customElements.define('lainly-cart-six', LainlyCartSix);

/**
 * <lainly-cart-match>: the cart's "You'll Love These" cards (snippets/lainly-cart-match.liquid), one per slide.
 * Choosing a color shows its photo; "Add" adds that piece to the cart and the cart re-renders (the card goes once
 * it is in). The dots follow the swipe, and a tap on a dot slides to its card.
 */
class LainlyCartMatch extends HTMLElement {
  #frame = 0;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('scroll', this.#onScroll, { capture: true });
    cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const track = this.querySelector('[data-match-track]');
      if (!(track instanceof HTMLElement) || !track.clientWidth) return;
      const index = Math.round(Math.abs(track.scrollLeft) / (track.clientWidth + 12));
      this.querySelectorAll('[data-match-dot]').forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    });
  };

  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-match-variant]')) return;
    const src = select.selectedOptions[0]?.dataset.image;
    const image = select.closest('[data-match-card]')?.querySelector('img');
    if (src && image instanceof HTMLImageElement) {
      image.removeAttribute('srcset');
      image.src = src;
    }
  };

  #onClick = async (event) => {
    const dot = event.target instanceof Element ? event.target.closest('[data-match-dot]') : null;
    if (dot instanceof HTMLElement) {
      const card = this.querySelectorAll('[data-match-card]')[Number(dot.dataset.matchDot)];
      const track = this.querySelector('[data-match-track]');
      if (card instanceof HTMLElement && track instanceof HTMLElement) {
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollTo({ left: card.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      }
      return;
    }
    const button = event.target instanceof Element ? event.target.closest('[data-match-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    const field = button.closest('[data-match-card]')?.querySelector('[data-match-variant]');
    const id = Number(field instanceof HTMLSelectElement || field instanceof HTMLInputElement ? field.value : 0);
    if (!id) return;

    button.setAttribute('aria-busy', 'true');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
      }).then((res) => res.json());
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );
      if (response.status) {
        this.dispatchEvent(new CartErrorEvent({ error: response.description || response.message, code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'lainly-cart-match', itemCount: 1 },
        });
        return;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, source: 'lainly-cart-match', itemCount: 1, sections: response.sections },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('lainly-cart-match')) customElements.define('lainly-cart-match', LainlyCartMatch);

/**
 * <lainly-cart-gift>: the gift box row above the cart total (snippets/lainly-cart-gift.liquid). It starts
 * unchecked: checking adds the box to the cart, unchecking takes it out. The checkbox always shows what is in the
 * cart (the row is drawn again with the cart after every change, and data-state, "pieces-boxes", says what is in
 * it now). With no pieces left the box is taken out too.
 */
let giftBusy = false;

// Earlier versions remembered an unchecked box in storage; nothing is remembered now.
try {
  localStorage.removeItem('lainly-gift-off');
  sessionStorage.removeItem('lainly-gift-off');
} catch {
  // Storage can be refused; nothing to clear then.
}

class LainlyCartGift extends HTMLElement {
  static observedAttributes = ['data-state'];

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    // Wait until the cart's own component is ready, so it hears any change and draws the cart again.
    customElements.whenDefined('cart-items-component').then(() => {
      if (this.isConnected) setTimeout(() => this.#sync(), 0);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  attributeChangedCallback(name, before, after) {
    if (this.isConnected && before !== null && before !== after) setTimeout(() => this.#sync(), 0);
  }

  get #pieces() {
    return Number((this.dataset.state || '0-0').split('-')[0]) || 0;
  }

  get #boxes() {
    return Number((this.dataset.state || '0-0').split('-')[1]) || 0;
  }

  /** The checkbox shows whether the box is in the cart; a box left alone without pieces is taken out. */
  #sync() {
    const check = this.querySelector('[data-gift-check]');
    if (check instanceof HTMLInputElement && !giftBusy) check.checked = this.#boxes > 0;
    if (this.#pieces === 0 && this.#boxes > 0) this.#remove();
  }

  #onChange = (event) => {
    const check = event.target;
    if (!(check instanceof HTMLInputElement) || !check.matches('[data-gift-check]')) return;
    if (check.checked && this.#boxes === 0) this.#add();
    else if (!check.checked && this.#boxes > 0) this.#remove();
  };

  #add() {
    const id = Number(this.dataset.variant);
    if (!id) return;
    this.#send('add', [{ merchandiseId: String(id), quantity: 1 }], Theme.routes.cart_add_url, {
      items: [{ id, quantity: 1 }],
    });
  }

  #remove() {
    const id = this.dataset.variant;
    if (!id) return;
    this.#send('remove', [{ id: this.dataset.key || id, quantity: 0 }], Theme.routes.cart_update_url, {
      updates: { [id]: 0 },
    });
  }

  /** Same request flow as the cart's other buttons, so the cart drawer and page redraw themselves. */
  async #send(action, lines, url, body) {
    if (giftBusy) return;
    giftBusy = true;
    this.setAttribute('aria-busy', 'true');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent(/** @type {any} */ ({ action, context: 'cart', lines, promise: deferred.promise }))
    );

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: sectionIds.join(',') }),
      }).then((res) => res.json());
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );
      const failed = Boolean(response.status || response.errors);
      if (failed) {
        this.dispatchEvent(
          new CartErrorEvent({ error: response.description || response.message || String(response.errors), code: 'INVALID' })
        );
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: failed,
          items: cart.items,
          source: 'lainly-cart-gift',
          itemCount: 1,
          ...(failed ? {} : { sections: response.sections }),
        },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      giftBusy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lainly-cart-gift')) customElements.define('lainly-cart-gift', LainlyCartGift);
